/**
 * store/library.ts — the «библиотека» slice: what the «Треки» tab shows and where in it we are.
 *
 * Task 05. `spec.md` «Плеер»: «Хранилище состояния (zustand) разбито на срезы: сессия, плеер,
 * очередь, библиотека, видео — чтобы перерисовка списка не перерисовывала плеер». So this is
 * its own store, not a key inside a shared one. A component that renders the player
 * subscribes to `store/player.ts` and never to this file, and three thousand rows arriving
 * here notify nobody over there. `apps/web/test/tracks.test.tsx` holds that line.
 *
 * ## Lists are keyed by what they list
 *
 * All tracks, one artist's, one riddim's, one tag's — four kinds of list, each cached under
 * {@link listKey}. Opening an artist, going back and opening them again is one request, not
 * two, and it lands on the rows already scrolled past. Paging appends to the list it belongs
 * to and never to a neighbour.
 *
 * ## One request per list at a time
 *
 * The virtual list asks for the next page whenever its window nears the end, which is on
 * every scroll event near the bottom. Without a guard a flick would send the same cursor ten
 * times and append the same page ten times. The guard is module-level, for the reason
 * `store/session.ts` gives: it is not state anything renders.
 *
 * ## Navigation lives here too
 *
 * Which section is open and which artist, riddim or tag list is on top of it survive a trip
 * to another tab and back. Keeping them in the component would reset the tab to «Все треки»
 * every time somebody looked at «Поиск».
 *
 * ## «Моё» keeps its two lists here too (task 11)
 *
 * The favourites and the recent tracks are lists like any other — the server answers them in
 * the page shape of `/api/tracks` — so they are cached under their own keys (`favorites`,
 * `recent`) and paged by the same code. Which of the two the tab shows is remembered like the
 * section of «Треки»; the rows are not — a section left is forgotten (`screens/My.tsx`).
 */

import { create } from 'zustand';

import { fetchArtists, fetchRiddims, fetchTracks } from '../lib/api';
import type {
  ApiFailure,
  ArtistItem,
  RiddimItem,
  TrackItem,
  TrackPage,
  TrackQuery,
} from '../lib/api';

import { useSession } from './session';

/* ────────────────────────────────────────────────────────────────────────────
 * Shapes
 * ──────────────────────────────────────────────────────────────────────────── */

/** A list the tab can open on top of a section: one artist's, riddim's or tag's tracks. */
export type Collection =
  | { readonly kind: 'artist'; readonly key: string; readonly name: string }
  | { readonly kind: 'riddim'; readonly id: number; readonly name: string }
  | { readonly kind: 'tag'; readonly id: number; readonly name: string };

/** The three sections of the tab, in the order the switch draws them. */
export type LibrarySection = 'tracks' | 'artists' | 'riddims';

/** Task 11: the two sections of «Моё». */
/** Task 51 added «Новое», the tracks of the last seven days. */
export type MySection = 'favorites' | 'recent' | 'fresh';

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface TrackList {
  readonly items: readonly TrackItem[];
  readonly nextCursor: string | null;
  /** From the first page. */
  readonly total: number | undefined;
  /** Of the first page. A later page failing does not throw away what is on screen. */
  readonly status: LoadStatus;
  readonly failure: ApiFailure | undefined;
  readonly loadingMore: boolean;
  /** A later page failed: the rows stay, and the bottom of the list offers «Ещё раз». */
  readonly moreFailed: boolean;
  /** `Date.now()` of the first page. */
  readonly fetchedAt: number;
}

export interface SectionList<T> {
  readonly items: readonly T[];
  readonly status: LoadStatus;
  readonly failure: ApiFailure | undefined;
}

export interface LibraryState {
  readonly section: LibrarySection;
  /** The section «Моё» shows. */
  readonly mySection: MySection;
  /** The list on top of the section, or `undefined` for the section itself. */
  readonly collection: Collection | undefined;
  readonly lists: Readonly<Record<string, TrackList>>;
  readonly artists: SectionList<ArtistItem>;
  readonly riddims: SectionList<RiddimItem>;

  readonly showSection: (section: LibrarySection) => void;
  readonly showMySection: (section: MySection) => void;
  readonly openCollection: (collection: Collection) => void;
  /**
   * Task 10: opens an artist's or a riddim's list on top of ITS section — «Исполнители» or
   * «Riddim» — so that «Назад» lands where that list lives, not wherever the tab was left. A
   * tag has no section and stays on top of the current one.
   */
  readonly openInSection: (collection: Collection) => void;
  readonly closeCollection: () => void;
  /**
   * The first page, unless the list is already here. `maxAgeMs` makes a list older than that
   * count as absent — the tab asks for it on entry, so a post published a minute ago is on
   * the list the next time somebody opens «Треки».
   */
  readonly loadList: (query: TrackQuery, options?: { maxAgeMs?: number }) => Promise<void>;
  /**
   * The next page. After a failed page it does nothing until asked with `retry`: the list
   * asks on every scroll event near the bottom, and without the brake a phone with no signal
   * would send a request per scroll event for as long as the thumb kept moving.
   */
  readonly loadMore: (query: TrackQuery, options?: { retry?: boolean }) => Promise<void>;
  readonly loadArtists: () => Promise<void>;
  readonly loadRiddims: () => Promise<void>;
  /**
   * Task 11: drops a cached list, so that the next visit starts from the server's answer and
   * not from the rows that were on screen the time before.
   */
  readonly forgetList: (query: TrackQuery) => void;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Keys
 * ──────────────────────────────────────────────────────────────────────────── */

/** The cache key of a list. Names are not part of it: two spellings are one list. */
export function listKey(query: TrackQuery): string {
  switch (query.kind) {
    case 'all':
      return 'all';
    case 'artist':
      return `artist:${query.key}`;
    case 'riddim':
      return `riddim:${String(query.id)}`;
    case 'tag':
      return `tag:${String(query.id)}`;
    case 'favorites':
    case 'recent':
      return query.kind;
    case 'fresh':
      return `fresh:${String(query.since)}`;
  }
}

/**
 * Task 44: the list a {@link listKey} names, back again — for a queue that goes on down the list
 * it was built from, on this launch or the next. `undefined` for a key no list has (a key from a
 * newer app, a hand-made one) and for «Недавние», which is one page and has nothing after it.
 */
export function queryOfKey(key: string): TrackQuery | undefined {
  if (key === 'all' || key === 'favorites') return { kind: key };
  const [kind, rest] = [key.slice(0, key.indexOf(':')), key.slice(key.indexOf(':') + 1)];
  if (kind === 'artist' && rest !== '') return { kind: 'artist', key: rest };
  if ((kind === 'riddim' || kind === 'tag') && /^[1-9]\d{0,14}$/u.test(rest)) {
    return { kind, id: Number(rest) };
  }
  if (kind === 'fresh' && /^[1-9]\d{0,14}$/u.test(rest)) return { kind, since: Number(rest) };
  return undefined;
}

/** What the server is asked for a collection. The name is for the screen only. */
export function queryOf(collection: Collection | undefined): TrackQuery {
  if (collection === undefined) return { kind: 'all' };
  if (collection.kind === 'artist') return { kind: 'artist', key: collection.key };
  return { kind: collection.kind, id: collection.id };
}

const EMPTY_SECTION = Object.freeze({ items: [], status: 'idle', failure: undefined } as const);

function token(): string | undefined {
  return useSession.getState().session?.token;
}

/** Appends a page, dropping any id already held: a post edited between two pages can recur. */
function appended(existing: readonly TrackItem[], page: TrackPage): TrackItem[] {
  const seen = new Set(existing.map((item) => item.id));
  return [...existing, ...page.items.filter((item) => !seen.has(item.id))];
}

/* ────────────────────────────────────────────────────────────────────────────
 * The store
 * ──────────────────────────────────────────────────────────────────────────── */

const inFlight = new Map<string, Promise<void>>();

/** Runs `work` unless the same `key` is already running, in which case waits for that one. */
async function once(key: string, work: () => Promise<void>): Promise<void> {
  const running = inFlight.get(key);
  if (running !== undefined) {
    await running;
    return;
  }
  const attempt = work();
  inFlight.set(key, attempt);
  try {
    await attempt;
  } finally {
    inFlight.delete(key);
  }
}

export const useLibrary = create<LibraryState>((set, get) => {
  const patchList = (key: string, patch: Partial<TrackList>): void => {
    const current = get().lists[key];
    if (current === undefined) return;
    set({ lists: { ...get().lists, [key]: { ...current, ...patch } } });
  };

  return {
    section: 'tracks',
    mySection: 'favorites',
    collection: undefined,
    lists: {},
    artists: EMPTY_SECTION,
    riddims: EMPTY_SECTION,

    showSection: (section) => {
      set({ section, collection: undefined });
    },
    showMySection: (mySection) => {
      set({ mySection });
    },
    openCollection: (collection) => {
      set({ collection });
    },
    openInSection: (collection) => {
      if (collection.kind === 'artist') set({ section: 'artists', collection });
      else if (collection.kind === 'riddim') set({ section: 'riddims', collection });
      else set({ collection });
    },
    closeCollection: () => {
      set({ collection: undefined });
    },

    loadList: async (query, options = {}) => {
      const key = listKey(query);
      const existing = get().lists[key];
      const maxAge = options.maxAgeMs ?? Number.POSITIVE_INFINITY;
      if (
        existing !== undefined &&
        existing.status !== 'failed' &&
        Date.now() - existing.fetchedAt < maxAge
      ) {
        return;
      }

      await once(`first:${key}`, async () => {
        // A list already on screen stays on screen while its refresh is in flight: blanking
        // three thousand rows to show «loading» for a quarter of a second reads as a crash.
        if (existing === undefined || existing.status === 'failed') {
          set({
            lists: {
              ...get().lists,
              [key]: {
                items: [],
                nextCursor: null,
                total: undefined,
                status: 'loading',
                failure: undefined,
                loadingMore: false,
                moreFailed: false,
                fetchedAt: Date.now(),
              },
            },
          });
        }

        const result = await fetchTracks(token(), query);
        if (!result.ok) {
          set({
            lists: {
              ...get().lists,
              [key]: {
                items: existing?.status === 'ready' ? existing.items : [],
                nextCursor: existing?.status === 'ready' ? existing.nextCursor : null,
                total: existing?.total,
                status: existing?.status === 'ready' ? 'ready' : 'failed',
                failure: result.error,
                loadingMore: false,
                moreFailed: false,
                fetchedAt: existing?.fetchedAt ?? Date.now(),
              },
            },
          });
          return;
        }

        set({
          lists: {
            ...get().lists,
            [key]: {
              items: result.data.items,
              nextCursor: result.data.nextCursor,
              total: result.data.total,
              status: 'ready',
              failure: undefined,
              loadingMore: false,
              moreFailed: false,
              fetchedAt: Date.now(),
            },
          },
        });
      });
    },

    loadMore: async (query, options = {}) => {
      const key = listKey(query);
      const list = get().lists[key];
      if (list?.status !== 'ready' || list.nextCursor === null) return;
      if (list.moreFailed && options.retry !== true) return;
      const cursor = list.nextCursor;

      await once(`more:${key}`, async () => {
        patchList(key, { loadingMore: true, moreFailed: false });
        const result = await fetchTracks(token(), query, cursor);
        const now = get().lists[key];
        // The list was replaced by a refresh while this page was in flight: the cursor
        // belongs to rows that are gone, and appending would splice two lists together.
        if (now?.nextCursor !== cursor) {
          patchList(key, { loadingMore: false });
          return;
        }
        if (!result.ok) {
          patchList(key, { loadingMore: false, moreFailed: true });
          return;
        }
        patchList(key, {
          items: appended(now.items, result.data),
          nextCursor: result.data.nextCursor,
          loadingMore: false,
        });
      });
    },

    loadArtists: async () => {
      if (get().artists.status === 'ready') return;
      await once('artists', async () => {
        set({ artists: { items: get().artists.items, status: 'loading', failure: undefined } });
        const result = await fetchArtists(token());
        set({
          artists: result.ok
            ? { items: result.data.items, status: 'ready', failure: undefined }
            : { items: [], status: 'failed', failure: result.error },
        });
      });
    },

    forgetList: (query) => {
      const key = listKey(query);
      if (get().lists[key] === undefined) return;
      set({ lists: Object.fromEntries(Object.entries(get().lists).filter(([k]) => k !== key)) });
    },

    loadRiddims: async () => {
      if (get().riddims.status === 'ready') return;
      await once('riddims', async () => {
        set({ riddims: { items: get().riddims.items, status: 'loading', failure: undefined } });
        const result = await fetchRiddims(token());
        set({
          riddims: result.ok
            ? { items: result.data.items, status: 'ready', failure: undefined }
            : { items: [], status: 'failed', failure: result.error },
        });
      });
    },
  };
});
